import { useCallback } from 'react'
import { useSearchParams } from 'react-router'

import type { ProductStatus } from './productStatusConfig'

const VALID_STATUSES: ReadonlySet<string> = new Set<ProductStatus>([
  'DRAFT',
  'PUBLISHED',
  'ARCHIVED',
  'SOLD_OUT',
])

function parseStatus(value: string | null): ProductStatus | '' {
  if (value && VALID_STATUSES.has(value)) {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- validated by Set lookup
    return value as ProductStatus
  }
  return ''
}

function parsePage(value: string | null): number {
  const num = Number(value)
  return Number.isInteger(num) && num >= 1 ? num : 1
}

function buildParams(status: ProductStatus | '', name: string, page: number): URLSearchParams {
  const params = new URLSearchParams()
  if (status) params.set('status', status)
  if (name) params.set('name', name)
  if (page > 1) params.set('page', String(page))
  return params
}

/**
 * The query string is the single source of truth for the 상품 목록 filters — see
 * `useOrderListUrlSync` for why mirroring component state back into the URL was wrong.
 */
export function useProductListUrlSync() {
  const [searchParams, setSearchParams] = useSearchParams()

  const status = parseStatus(searchParams.get('status'))
  const name = searchParams.get('name') ?? ''
  const currentPage = parsePage(searchParams.get('page'))

  // Changing a filter always starts back on page 1.
  const setStatus = useCallback(
    (next: ProductStatus | '') => {
      setSearchParams(buildParams(next, name, 1))
    },
    [setSearchParams, name],
  )

  // The name box fires on every keystroke, so replace instead of pushing a history entry per character.
  const setName = useCallback(
    (next: string) => {
      setSearchParams(buildParams(status, next, 1), { replace: true })
    },
    [setSearchParams, status],
  )

  const resetFilters = useCallback(() => {
    setSearchParams(new URLSearchParams())
  }, [setSearchParams])

  const setPage = useCallback(
    (page: number) => {
      setSearchParams(buildParams(status, name, page))
    },
    [setSearchParams, status, name],
  )

  return { status, name, currentPage, setStatus, setName, setPage, resetFilters } as const
}
